class MegaMenu extends HTMLElement {
  connectedCallback() {
    this.setupEventListeners();
  }

  setupEventListeners() {
    const megaMenuNav = document.querySelector('[data-mega-menu]');
    if (!megaMenuNav) return;

    const links = megaMenuNav.querySelectorAll('.mega-menu__link[aria-expanded]');

    links.forEach((link) => {
      link.addEventListener('click', (e) => this.toggleSubmenu(e));
      link.addEventListener('mouseenter', () => this.showSubmenu(link));
      link.addEventListener('mouseleave', () => this.hideSubmenu(link));
    });
  }

  toggleSubmenu(e) {
    e.preventDefault();
    const link = e.currentTarget;
    const isExpanded = link.getAttribute('aria-expanded') === 'true';

    if (isExpanded) {
      this.hideSubmenu(link);
    } else {
      this.showSubmenu(link);
    }
  }

  showSubmenu(link) {
    const submenuId = link.getAttribute('aria-controls');
    const submenu = this.querySelector(`#${submenuId}`);

    if (submenu) {
      link.setAttribute('aria-expanded', 'true');
      submenu.removeAttribute('hidden');
    }
  }

  hideSubmenu(link) {
    const submenuId = link.getAttribute('aria-controls');
    const submenu = this.querySelector(`#${submenuId}`);

    if (submenu) {
      link.setAttribute('aria-expanded', 'false');
      submenu.setAttribute('hidden', '');
    }
  }
}

if (!customElements.get('mega-menu')) {
  customElements.define('mega-menu', MegaMenu);
}

// Suppress spurious mouseleave closes caused by native context menu or tab-switch
let _contextMenuOpen = false;
document.addEventListener('contextmenu', () => { _contextMenuOpen = true; });
document.addEventListener('mousemove', () => { _contextMenuOpen = false; }, { capture: true });
document.addEventListener('mousedown', () => { _contextMenuOpen = false; }, { capture: true });

function _shouldSuppressClose() {
  return _contextMenuOpen || !document.hasFocus();
}

const TOP_LEVEL_MEGA_SCREEN_SELECTOR =
  '.mega-menu__screen:not(.mega-menu__screen--level-two):not(.mega-menu__screen--level-three)[data-menu-item]';

function syncHeaderTriggerAriaExpanded(menuItemValue, expanded) {
  document.querySelectorAll('.header__menu-item[data-menu-item]').forEach((item) => {
    const link = item.querySelector('.header__menu-link, a');
    if (!link?.hasAttribute('aria-controls')) return;
    const key = item.getAttribute('data-menu-item');
    const isExpanded = expanded && key === menuItemValue;
    link.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');
  });
}

function resetAllHeaderTriggerAriaExpanded() {
  document
    .querySelectorAll(
      '.header__menu-item[data-menu-item] .header__menu-link[aria-controls], .header__menu-item[data-menu-item] a[aria-controls]'
    )
    .forEach((link) => {
      link.setAttribute('aria-expanded', 'false');
    });
}

// Close desktop mega menu - shared logic (no mobile/is-mobile)
function closeDesktopMegaMenu() {
  const megaMenuSection = document.querySelector('.mega-menu-section');
  if (!megaMenuSection) return;

  resetAllHeaderTriggerAriaExpanded();
  megaMenuSection.classList.remove('is-visible');
  document.querySelector('.header--transparent')?.classList.remove('menu-open');
  megaMenuSection
    .querySelectorAll(TOP_LEVEL_MEGA_SCREEN_SELECTOR)
    .forEach((item) => {
      item.classList.remove('is-active');
      item.setAttribute('aria-expanded', 'false');
      item.querySelector('.mega-menu__screen-title')?.setAttribute('aria-expanded', 'false');
    });
}

// Setup header menu sync
function setupHeaderMenuSync() {
  try {
    const megaMenuSection = document.querySelector('.mega-menu-section');
    const headerMenuItems = document.querySelectorAll('.header__menu-item[data-menu-item]');

    if (!megaMenuSection || headerMenuItems.length === 0) {
      return;
    }

    headerMenuItems.forEach((headerItem) => {
      const menuItemValue = headerItem.getAttribute('data-menu-item');
      const headerLink = headerItem.querySelector('.header__menu-link, a');
      const matchedScreen = menuItemValue
        ? megaMenuSection.querySelector(
            `${TOP_LEVEL_MEGA_SCREEN_SELECTOR}[data-menu-item="${menuItemValue}"]`
          )
        : null;
      // Desktop: aria-controls / initial aria-expanded come from header-menu.liquid when menu_style is mega_menu.
      if (headerLink && matchedScreen?.id && !headerLink.hasAttribute('aria-controls')) {
        headerLink.setAttribute('aria-controls', matchedScreen.id);
      }
      if (headerLink && matchedScreen && !headerLink.hasAttribute('aria-expanded')) {
        headerLink.setAttribute('aria-expanded', 'false');
      }

      // Helper function to show menu for an item
      const showMenuForItem = () => {
        const megaMenuItems = megaMenuSection.querySelectorAll(TOP_LEVEL_MEGA_SCREEN_SELECTOR);
        let foundMatch = false;
        megaMenuItems.forEach((item, index) => {
          const itemDataAttr = item.getAttribute('data-menu-item');
          if (itemDataAttr === menuItemValue) {
            item.classList.add('is-active');
            item.setAttribute('aria-expanded', 'true');
            const titleElement = item.querySelector('.mega-menu__screen-title');
            if (titleElement) {
              titleElement.setAttribute('aria-expanded', 'true');
            }
            foundMatch = true;
          } else {
            item.classList.remove('is-active');
            item.setAttribute('aria-expanded', 'false');
            const titleElement = item.querySelector('.mega-menu__screen-title');
            if (titleElement) {
              titleElement.setAttribute('aria-expanded', 'false');
            }
          }
        });
        syncHeaderTriggerAriaExpanded(menuItemValue, foundMatch);
        // Show mega-menu-section if there's a match
        if (foundMatch) {
          megaMenuSection.classList.add('is-visible');
          // Sync menu-open class to .header--transparent
          const headerTransparent = document.querySelector('.header--transparent');
          if (headerTransparent) {
            headerTransparent.classList.add('menu-open');
          }
        } else {
          closeDesktopMegaMenu();
        }
      };

      // Trigger on mouseenter
      headerItem.addEventListener('mouseenter', showMenuForItem);

      // First-tap intercept for touch devices (e.g. iPad Safari/Chrome): hover doesn't fire on tap,
      // so the first tap would navigate. Intercept on touchstart so the browser never gets a
      // navigating click on first tap; second tap allows default and navigates.
      const link = headerItem.querySelector('.header__menu-link, a');
      const hasMegaMenu = megaMenuSection.querySelector(
        `${TOP_LEVEL_MEGA_SCREEN_SELECTOR}[data-menu-item="${menuItemValue}"]`
      );
      if (link && hasMegaMenu) {
        const handleFirstTap = (e) => {
          if (window.innerWidth < 749) return;
          const isMenuVisible = megaMenuSection.classList.contains('is-visible');
          const isThisItemActive = megaMenuSection.querySelector(
            `.mega-menu__screen.is-active[data-menu-item="${menuItemValue}"]`
          );
          if (isMenuVisible && isThisItemActive) return;
          e.preventDefault();
          showMenuForItem();
        };
        link.addEventListener('touchstart', handleFirstTap, { passive: false, capture: true });
        link.addEventListener('click', handleFirstTap, { capture: true });
      }

      // Trigger on focus (keyboard navigation)
      headerItem.addEventListener('focus', showMenuForItem);

      // Handle keyboard navigation (Enter/Space to expand menu when a mega screen exists).
      // Leaf header links must keep default behavior so Enter navigates (keydown bubbles from <a>).
      headerItem.addEventListener('keydown', (/** @type {KeyboardEvent} */ e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          if (!hasMegaMenu) return;
          e.preventDefault();
          showMenuForItem();

          // Move focus into the expanded menu
          const menuItemValue = headerItem.getAttribute('data-menu-item');
          const screen = megaMenuSection.querySelector(
            `.mega-menu__screen[data-menu-item="${menuItemValue}"]`
          );
          if (screen) {
            const firstLink = screen.querySelector('a');
            if (firstLink) {
              firstLink.focus();
            }
          }
        } else if (e.key === 'Escape') {
          const isVisible = megaMenuSection.classList.contains('is-visible');
          if (isVisible) {
            e.preventDefault();
            closeDesktopMegaMenu();
          }
        }
      });

      // Handle blur/focus leaving the menu (for keyboard navigation)
      headerItem.addEventListener('keydown', (/** @type {KeyboardEvent} */ e) => {
        if (e.key === 'Tab') {
          // Delay check to allow focus to move first
          setTimeout(() => {
            const activeElement = document.activeElement;
            const allHeaderItems = document.querySelectorAll('.header__menu-item[data-menu-item]');
            const isMenuItemFocused = Array.from(allHeaderItems).some(item => item === activeElement);
            const isMegaMenuFocused = megaMenuSection.contains(activeElement);

            if (!isMenuItemFocused && !isMegaMenuFocused) {
              closeDesktopMegaMenu();
            }
          }, 0);
        }
      });

      headerItem.addEventListener('mouseleave', (e) => {
        // If mouse is moving into mega menu, don't close (prevents auto-close when moving cursor down)
        if (e.relatedTarget && megaMenuSection.contains(e.relatedTarget)) {
          return;
        }

        // On mouseleave, check if any header item or the mega menu section is still being hovered
        let anyHovered = false;
        const allHeaderItems = document.querySelectorAll('.header__menu-item[data-menu-item]');

        // Use 120ms delay to allow mouse to reach mega menu (setTimeout(0) was too fast - caused auto-close)
        setTimeout(() => {
          if (_shouldSuppressClose()) return;

          allHeaderItems.forEach((item) => {
            if (item.matches(':hover')) {
              anyHovered = true;
            }
          });

          // Also check if mega menu section is being hovered
          if (megaMenuSection.matches(':hover')) {
            anyHovered = true;
          }

          if (!anyHovered) closeDesktopMegaMenu();
        }, 120);
      });
    });

    // Add mouseleave handler to the mega menu section itself
    if (megaMenuSection) {
      megaMenuSection.addEventListener('mouseleave', () => {
        if (_shouldSuppressClose()) return;

        let anyHovered = false;
        const allHeaderItems = document.querySelectorAll('.header__menu-item[data-menu-item]');

        allHeaderItems.forEach((item) => {
          if (item.matches(':hover')) {
            anyHovered = true;
          }
        });

        if (!anyHovered) closeDesktopMegaMenu();
      });
    }

    // Add Esc key listener: close mega menu and return focus to trigger button
    if (megaMenuSection) {
      megaMenuSection.addEventListener('keydown', (/** @type {KeyboardEvent} */ e) => {
        if (e.key === 'Escape') {
          const isVisible = megaMenuSection.classList.contains('is-visible');
          if (isVisible) {
            e.preventDefault();
            const activeScreen = megaMenuSection.querySelector(
              '.mega-menu__screen.is-active[data-menu-item]'
            );
            const menuItemValue = activeScreen?.getAttribute('data-menu-item');
            closeDesktopMegaMenu();
            const triggerButton = menuItemValue
              ? document.querySelector(
                  `.header__menu-item[data-menu-item="${menuItemValue}"] .header__menu-link, .header__menu-item[data-menu-item="${menuItemValue}"] a`
                )
              : null;
            if (triggerButton instanceof HTMLElement) triggerButton.focus();
          }
        }
      });
    }

    // Add Tab key listener: when tabbing from last submenu item, move focus to next parent menu item
    if (megaMenuSection) {
      megaMenuSection.addEventListener('keydown', (/** @type {KeyboardEvent} */ e) => {
        if (e.key !== 'Tab') return;

        const activeElement = document.activeElement;
        if (!activeElement || !megaMenuSection.contains(activeElement)) return;

        const activeScreen = megaMenuSection.querySelector(
          '.mega-menu__screen.is-active[data-menu-item]'
        );
        if (!activeScreen) return;

        const focusables = Array.from(
          activeScreen.querySelectorAll(
            'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
          )
        ).filter((el) => el.offsetParent !== null);

        const isLastFocusable =
          !e.shiftKey && focusables.length > 0 && activeElement === focusables[focusables.length - 1];

        if (isLastFocusable) {
          e.preventDefault();
          const currentMenuItemValue = activeScreen.getAttribute('data-menu-item');
          const allHeaderItems = document.querySelectorAll(
            '.header__menu-item[data-menu-item]'
          );
          const currentIndex = Array.from(allHeaderItems).findIndex(
            (item) => item.getAttribute('data-menu-item') === currentMenuItemValue
          );
          const nextHeaderItem = allHeaderItems[currentIndex + 1];
          const nextLink =
            nextHeaderItem?.querySelector('.header__menu-link, a') ?? null;
          closeDesktopMegaMenu();
          if (nextLink instanceof HTMLElement) {
            nextLink.focus();
          }
          return;
        }

        // When tabbing out elsewhere, close menu after focus moves
        setTimeout(() => {
          const newActive = document.activeElement;
          const allHeaderItems = document.querySelectorAll(
            '.header__menu-item[data-menu-item]'
          );
          const isMenuItemFocused = Array.from(allHeaderItems).some(
            (item) => item === newActive
          );
          const isMegaMenuFocused = megaMenuSection.contains(newActive);

          if (!isMenuItemFocused && !isMegaMenuFocused) closeDesktopMegaMenu();
        }, 0);
      });
    }
  } catch (err) {}
}

// Document-level Escape handler — works even when focus is outside the nav (hover-opened)
document.addEventListener('keydown', (/** @type {KeyboardEvent} */ e) => {
  if (e.key !== 'Escape') return;
  const megaMenuSection = document.querySelector('.mega-menu-section');
  if (!megaMenuSection || !megaMenuSection.classList.contains('is-visible')) return;
  e.preventDefault();
  closeDesktopMegaMenu();
});

// Call immediately and on DOM ready - hide mega menu on load
closeDesktopMegaMenu();
setupHeaderMenuSync();

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', setupHeaderMenuSync);
}

// Position mega menu section to stick to bottom of header component
function updateMegaMenuPosition() {
  const headerComponent = document.querySelector('header-component');
  const megaMenuSection = document.querySelector('.mega-menu-section');

  if (!headerComponent || !megaMenuSection) return;

  const headerRect = headerComponent.getBoundingClientRect();
  const headerBottom = headerRect.bottom;

  // Position relative to viewport, pinned to header bottom
  megaMenuSection.style.position = 'fixed';
  megaMenuSection.style.top = headerBottom - 10 + 'px';
  megaMenuSection.style.left = '0';
  megaMenuSection.style.right = '0';
}

// Update on load, resize, and scroll
updateMegaMenuPosition();
window.addEventListener('resize', updateMegaMenuPosition);
window.addEventListener('scroll', updateMegaMenuPosition);

// Watch for header mutations
const headerComponent = document.querySelector('header-component');
if (headerComponent) {
  const observer = new MutationObserver(updateMegaMenuPosition);
  observer.observe(headerComponent, { attributes: true, subtree: true });
}

// Initialize aria attributes for menu items with submenus
function initializeAriaAttributes() {
  const megaMenuScreens = document.querySelectorAll('.mega-menu__screen[data-menu-item]');

  megaMenuScreens.forEach((screen, index) => {
    const submenu = screen.querySelector('.mega-menu__wrapper');
    if (submenu) {
      const screenId = `mega-menu-screen-${index}`;
      const titleElement = screen.querySelector('.mega-menu__screen-title');

      if (titleElement) {
        // Set aria attributes on the title element
        titleElement.setAttribute('aria-expanded', 'false');
        titleElement.setAttribute('aria-controls', screenId);
        titleElement.id = screenId;
        titleElement.role = 'button';
        titleElement.tabIndex = '0';
      }

      // Also set aria-expanded on the screen itself based on is-visible class
      const isVisible = screen.classList.contains('is-visible');
      screen.setAttribute('aria-expanded', isVisible ? 'true' : 'false');
    }
  });
}

// Reset when closing mobile menu - aria-expanded, classes, open attr
function resetMobileMenuState() {
  resetAllHeaderTriggerAriaExpanded();
  const megaMenuSection = document.querySelector('.mega-menu-section');
  if (megaMenuSection) {
    megaMenuSection.classList.remove(
      'is-mobile',
      'is-visible',
      'menu-drawer--open',
      'menu-drawer--closing',
      'menu-drawer--closed'
    );
  }

  document.querySelector('.header--transparent')?.classList.remove('menu-open');

  const drawerDetails =
    document.querySelector('#Details-menu-drawer-container') ||
    document.querySelector('header-drawer details');
  if (drawerDetails) {
    drawerDetails.classList.remove('menu-open');
    drawerDetails.removeAttribute('open');
    drawerDetails.querySelector('summary')?.setAttribute('aria-expanded', 'false');
  }

  document.querySelectorAll('.mega-menu__screen').forEach((screen) => {
    screen.classList.remove('is-active', 'is-visible');
    screen.setAttribute('aria-expanded', 'false');
    screen.querySelector('.mega-menu__screen-title')?.setAttribute('aria-expanded', 'false');
  });

  document.querySelectorAll('.mega-menu__submenu-column').forEach((col) => {
    col.classList.remove('is-visible');
  });

  document.querySelectorAll('.mega-menu-section details[open], header-drawer details[open]').forEach((d) => {
    d.removeAttribute('open');
    d.querySelector('summary')?.setAttribute('aria-expanded', 'false');
  });
}

// Mobile menu click handler
document.addEventListener('click', (e) => {
  if (window.innerWidth >= 749) return; // Only on mobile
  const target = e.target instanceof Element ? e.target : null;
  if (!target) return;

  // Handle back button clicks
  if (target.closest('.menu-drawer__back-button')) {
    const details = document.querySelector('details.drawer-localization');
    if (details) {
      details.removeAttribute('open');
    }
    return;
  }

  if (
    target.closest('[data-mega-menu-close]') ||
    target.closest('.mega-menu__controls-icon--close') ||
    target.closest('.menu-drawer__close-button')
  ) {
    const headerDrawer = document.querySelector('header-drawer');
    if (headerDrawer && typeof headerDrawer.close === 'function') {
      headerDrawer.close();
    } else {
      resetMobileMenuState();
    }
    return;
  }

  // Leaf links inside submenu list: let them navigate naturally
  if (target.closest('.mega-menu__submenu-list') && target.closest('a')) {
    return;
  }

  // Handle submenu column title taps: first tap expands, second tap navigates
  const columnTitle = target.closest('.mega-menu__submenu-title');
  if (columnTitle) {
    const column = columnTitle.closest('.mega-menu__submenu-column');
    if (column) {
      e.preventDefault();
      e.stopPropagation();
      if (column.classList.contains('is-visible')) {
        const titleLink = column.querySelector('.mega-menu__submenu-title a[href]');
        if (titleLink && titleLink.getAttribute('href') !== 'javascript:;') {
          window.location.href = titleLink.href;
          return;
        }
      }
      column.classList.toggle('is-visible');
      return;
    }
  }

  // Handle screen title taps: first tap expands, second tap navigates
  const titleElement = target.closest('.mega-menu__screen-title');
  if (titleElement) {
    const screen = titleElement.closest('.mega-menu__screen');
    if (screen) {
      if (screen.classList.contains('is-visible')) {
        const menuItemValue = screen.getAttribute('data-menu-item');
        const headerLink = menuItemValue
          ? document.querySelector(`.header__menu-item[data-menu-item="${menuItemValue}"] a[href]`)
          : null;
        if (headerLink && headerLink.getAttribute('href') !== '#') {
          window.location.href = headerLink.href;
          return;
        }
      }
      screen.classList.toggle('is-visible');
      const isNowVisible = screen.classList.contains('is-visible');
      titleElement.setAttribute('aria-expanded', isNowVisible ? 'true' : 'false');
      screen.setAttribute('aria-expanded', isNowVisible ? 'true' : 'false');
      e.stopPropagation();
      return;
    }
  }

  // If clicking on subtitle control, close all screens
  if (target.closest('.mega-menu__screen-subtitle')) {
    document.querySelectorAll('.mega-menu__screen').forEach((screen) => {
      screen.classList.remove('is-visible');
      screen.setAttribute('aria-expanded', 'false');
      const title = screen.querySelector('.mega-menu__screen-title');
      if (title) {
        title.setAttribute('aria-expanded', 'false');
      }
    });
    return;
  }

  const screen = target.closest('.mega-menu__screen');
  if (!screen) return;

  if (target.tagName === 'A') return; // Allow links to work

  screen.classList.toggle('is-visible');
  const isNowVisible = screen.classList.contains('is-visible');
  screen.setAttribute('aria-expanded', isNowVisible ? 'true' : 'false');
  const titleElement2 = screen.querySelector('.mega-menu__screen-title');
  if (titleElement2) {
    titleElement2.setAttribute('aria-expanded', isNowVisible ? 'true' : 'false');
  }
});

// Initialize on load
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeAriaAttributes);
} else {
  initializeAriaAttributes();
}
